import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { CSS, styled } from '@/stitches.config';

const ToggleHeaderStyled = styled(Box, {
  columnGap: '$space-4',
  transition: 'background-color 0.3s ease',
  variants: {
    variant: {
      gray: {
        backgroundColor: '$gray200',
      },
      white: {
        backgroundColor: '$white',
      },
      transparent: {
        backgroundColor: '$transparent',
      },
    },
    isDisable: {
      false: {
        cursor: 'pointer',
      },
      true: {
        cursor: 'auto',
      },
    },
    isOpen: {
      true: {
        '& i': {
          transform: 'rotate(180deg)',
        },
      },
    },
    isLine: {
      true: {
        '&:after': {
          content: '',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 1,
          backgroundColor: '$gray300',
        },
      },
    },
    isBody: {
      true: {},
    },
  },
  compoundVariants: [
    {
      isDisable: false,
      isOpen: false,
      css: {
        '@hover': {
          '&:hover': {
            backgroundColor: '$gray200',
          },
        },
      },
    },
    {
      variant: 'gray',
      isDisable: false,
      isOpen: false,
      css: {
        '@hover': {
          '&:hover': {
            backgroundColor: '$gray300',
          },
        },
      },
    },
    {
      isDisable: false,
      isOpen: true,
      css: {
        '@hover': {
          '&:hover': {
            '& span': {
              color: '$gray900',
            },
            '& i': {
              color: '$gray900',
            },
          },
        },
      },
    },
    {
      isLine: true,
      isOpen: true,
      css: {
        '&:after': {
          content: 'none',
        },
      },
    },
    {
      isLine: true,
      isDisable: false,
      isOpen: false,
      css: {
        '@hover': {
          '&:hover': {
            '&:after': {
              content: 'none',
            },
          },
        },
      },
    },
    {
      isDisable: true,
      variant: 'transparent',
      css: {
        px: '$space-0',
      },
    },
    // {
    //   isOpen: true,
    //   isBody: true,
    //   css: {
    //     borderBottomRightRadius: 0,
    //     borderBottomLeftRadius: 0,
    //   },
    // },
  ],
});

export type ToggleHeaderVariant = 'gray' | 'white' | 'transparent';
export type ToggleHeaderSize = 'base' | 'sm';
export type ToggleHeaderIconPosition = 'leading' | 'trailing';
export interface IToggleProps {
  title: string;
  isOpen: boolean;
  variant?: ToggleHeaderVariant;
  size?: ToggleHeaderSize;
  isDisable?: boolean;
  isLine?: boolean;
  isBody?: boolean;
  iconPosition?: ToggleHeaderIconPosition;
  dataTest?: string;
}

interface IProps extends IToggleProps {
  onClick?: (value: boolean) => void;
  css?: CSS;
}

export const ToggleHeader: React.FC<IProps> = ({
  title,
  isOpen,
  onClick,
  isLine,
  css,
  variant = 'gray',
  size = 'base',
  isDisable = false,
  iconPosition = 'trailing',
  isBody = true,
  dataTest = 'toggle_header',
}) => {
  const _onClick = () => {
    !isDisable && onClick && onClick(!isOpen);
  };

  return (
    <ToggleHeaderStyled
      position="relative"
      display="flex"
      paddingX="4"
      paddingY="4"
      alignItems="center"
      onClick={_onClick}
      isOpen={isOpen}
      isDisable={isDisable}
      variant={variant}
      isLine={isLine}
      isBody={isBody}
      css={{
        ...css,
        flexDirection: iconPosition === 'leading' ? 'row-reverse' : 'row',
      }}
      className={isOpen ? 'is-open' : ''}
      test={{
        dataTest: dataTest,
      }}
    >
      <Text
        variant={isOpen && !isDisable ? 'text-red' : 'text-gray-darkest'}
        size={size === 'base' ? 'text-base' : 'text-body-1'}
        font="bold"
        css={{
          flexGrow: 1,
        }}
      >
        {title}
      </Text>
      {!isDisable && (
        <Icon
          icon="expand-more"
          size="base"
          color={isOpen ? 'icon-red' : 'icon-gray'}
        />
      )}
    </ToggleHeaderStyled>
  );
};

ToggleHeader.displayName = 'ToggleHeader';
